import { useMemo } from 'react';
import { AxiosRequestConfig } from 'axios';
import { QueryKey, useQuery, useMutation, UseQueryOptions, UseMutationOptions } from 'react-query';

import { useApiContext } from './use-api-context';

/**
 * Marks a request as belonging to a pre-session ("public") flow — registration, password/MFA
 * recovery, anonymous email-link replies. The API interceptor reads this flag to exempt the
 * request from the no-session force-logout. It is an internal, non-transmitted config property
 * (axios forwards unknown config keys to interceptors but never serializes them onto the wire),
 * set ONLY by the createUsePublicApi* factories below — never by callers directly.
 */
declare module 'axios' {
  interface AxiosRequestConfig {
    isPublic?: boolean;
  }
  interface InternalAxiosRequestConfig {
    isPublic?: boolean;
  }
}

/**
 * Recursively trims all string values in an object
 * @param value - The value to trim (can be an object, array, or primitive)
 * @returns The trimmed value
 */
const trimObjectValues = (value: any): any => {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    return value.map(trimObjectValues);
  }

  if (value instanceof FormData || value instanceof Date) {
    return value;
  }

  if (typeof value === 'object') {
    return Object.keys(value).reduce(
      (acc, key) => ({
        ...acc,
        [key]: trimObjectValues(value[key]),
      }),
      {}
    );
  }

  return value;
};

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';
// TODO: Decide if we're sticking to deliberate null or just undefined throughout the app
/**
 * Options for making an API query.
 *
 * @template TResponse - The type of the response data.
 */
interface ApiQueryOptions<TResponse> extends UseQueryOptions<TResponse> {
  body?: any | null;
  queryParams?: Record<string, any | null> | null;
  pathParams?: Record<string, string | null> | null;
}

/**
 * Custom hook for making API queries using axios and react-query.
 *
 * @param key - The query key for react-query.
 * @param endpoint - The API endpoint to send the request to.
 * @param method - The HTTP method to use for the request (default: 'get').
 * @param options - Additional options for the useQuery hook (optional).
 * @returns The result of the query.
 */
const buildUseApiQuery =
  (isPublic: boolean) =>
  <TEndpoint extends string>() =>
  <TResponse = unknown>(
    key: QueryKey,
    endpoint: TEndpoint,
    method: HttpMethod = 'get',
    options: ApiQueryOptions<TResponse> = {}
  ) => {
    const client = useApiContext();

    const fetchData = useMemo(
      () => async () => {
        const { body, queryParams, pathParams } = options;
        const requestConfig: AxiosRequestConfig = {
          method,
          url: endpoint,
          ...(isPublic && { isPublic: true }),
        };
        if (body) {
          requestConfig.data = trimObjectValues(body);
        }
        if (queryParams) {
          requestConfig.params = queryParams;
        }

        if (pathParams) {
          Object.entries(pathParams).forEach(([objKey, value]) => {
            if (value !== null) {
              requestConfig.url = requestConfig.url?.replace(`:${objKey}`, value);
            }
          });
        }

        const response = await client.request<TResponse>(requestConfig);

        return response.data;
      },
      [client, endpoint, method, options]
    );

    return useQuery<TResponse>(key, fetchData, options);
  };

/** Standard authenticated query factory — requests require a valid session. */
export const createUseApiQuery = buildUseApiQuery(false);
/** Public query factory — requests run pre-session and are exempt from the no-session logout. */
export const createUsePublicApiQuery = buildUseApiQuery(true);

interface BaseVariables<TBody = unknown> {
  body?: TBody | FormData | null;
  pathParams?: Record<string, string> | null;
  queryParams?: Record<string, any> | null;
}

interface ApiMutationOptions<TResponse, TError, TVariables> extends UseMutationOptions<TResponse, TError, TVariables> {
  timeout?: number;
  responseType?: AxiosRequestConfig['responseType'];
}

/**
 * Custom hook for making API mutations using axios and react-query.
 *
 * @param endpoint - The API endpoint to send the request to.
 * @param method - The HTTP method to use for the request (default: 'post').
 * @param options - Additional options for the useMutation hook (optional).
 * @returns The result of the mutation.
 */
const buildUseApiMutation =
  (isPublic: boolean) =>
  <TEndpoint extends string>() =>
  <
    TResponse = unknown,
    TError = unknown,
    TBody = unknown,
    TVariables extends BaseVariables<TBody> = BaseVariables<TBody>,
  >(
    endpoint: TEndpoint,
    method: HttpMethod = 'post',
    options?: ApiMutationOptions<TResponse, TError, TVariables>
  ) => {
    const client = useApiContext();

    const sendRequest = useMemo(
      () => async (variables: BaseVariables<TBody>) => {
        const requestConfig: AxiosRequestConfig = {
          method,
          url: endpoint,
          ...(options?.timeout && { timeout: options.timeout }),
          ...(options?.responseType && { responseType: options.responseType }),
          ...(isPublic && { isPublic: true }),
        };

        if (variables.body instanceof FormData) {
          // Handle FormData for file uploads
          requestConfig.data = variables.body;
          requestConfig.headers = {
            ...requestConfig.headers,
            // 'Content-Type': 'multipart/form-data',
          };
        } else {
          requestConfig.data = trimObjectValues(variables.body);
        }

        if (variables.pathParams) {
          Object.entries(variables.pathParams).forEach(([objKey, value]) => {
            requestConfig.url = requestConfig.url?.replace(`:${objKey}`, value);
          });
        }

        if (variables.queryParams) {
          requestConfig.params = variables.queryParams;
        }

        const response = await client.request<TResponse>(requestConfig);
        return response.data;
      },
      [client, endpoint, method, options?.timeout, options?.responseType]
    );

    return useMutation<TResponse, TError, TVariables>(sendRequest, options);
  };

/** Standard authenticated mutation factory — requests require a valid session. */
export const createUseApiMutation = buildUseApiMutation(false);
/** Public mutation factory — requests run pre-session and are exempt from the no-session logout. */
export const createUsePublicApiMutation = buildUseApiMutation(true);
